import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import AILeadDetailDrawer from "@/components/AILeadDetailDrawer";
import SmartListsBar from "@/components/SmartListsBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Flame, Phone, MessageSquare, RefreshCw, ChevronRight, X, Sparkles } from "lucide-react";

// AI Lead Manager — single command center for AI Voice + AI SMS results.
// Read-only priority queue that surfaces the highest-motivation sellers
// coming out of the Retell + SMS AI systems, so acquisitions can act on
// them without listening to every recording or reading every reply.
const BUCKETS = [
  { key: "hot",     label: "🔥 HOT — Contact Immediately",   min: 80, tone: "border-red-300 bg-red-50" },
  { key: "warm",    label: "🟠 WARM — Follow Up Today",       min: 60, tone: "border-orange-300 bg-orange-50" },
  { key: "nurture", label: "🟡 NURTURE — Automated Follow-Up", min: 40, tone: "border-amber-200 bg-amber-50" },
  { key: "low",     label: "⚪ LOW — Long-Term Follow-Up",     min:  0, tone: "border-slate-200 bg-slate-50" },
];

function bucketOf(score) {
  if (score >= 80) return "hot";
  if (score >= 60) return "warm";
  if (score >= 40) return "nurture";
  return "low";
}
function relTime(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AILeadManager() {
  const [rows, setRows]         = useState([]);
  const [busy, setBusy]         = useState(false);
  const [filter, setFilter]     = useState("");
  const [activeBucket, setBkt]  = useState("all");
  // Smart-list filter — when a saved list is applied, we constrain the
  // queue to the intersection of the list's lead IDs. Clears with the X
  // chip next to the applied-list name.
  const [smartFilter, setSmartFilter] = useState(null);   // {id, name, count, leadIds}
  // In-place detail drawer state — DO NOT navigate away from the queue.
  const [openLeadId, setOpenLeadId]       = useState(null);
  const [openUnknownId, setOpenUnknownId] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/ai-lead-manager/queue");
      setRows(data?.rows || []);
    } catch { setRows([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const smartSet = smartFilter?.leadIds ? new Set(smartFilter.leadIds) : null;
    return rows
      .filter((r) => activeBucket === "all" || bucketOf(r.motivation_score || 0) === activeBucket)
      .filter((r) => !q || (r.name || "").toLowerCase().includes(q) || (r.property_address || "").toLowerCase().includes(q))
      .filter((r) => !smartSet || (r.lead_id && smartSet.has(r.lead_id)));
  }, [rows, filter, activeBucket, smartFilter]);

  const counts = useMemo(() => {
    const c = { all: rows.length, hot: 0, warm: 0, nurture: 0, low: 0 };
    for (const r of rows) c[bucketOf(r.motivation_score || 0)] += 1;
    return c;
  }, [rows]);

  const openLead = (r) => {
    if (r.lead_id) { setOpenLeadId(r.lead_id); setOpenUnknownId(null); }
    else if (r.unknown_inbound_id) { setOpenUnknownId(r.unknown_inbound_id); setOpenLeadId(null); }
  };
  const closeDrawer = ({ refetch } = {}) => {
    setOpenLeadId(null); setOpenUnknownId(null);
    if (refetch) load();
  };

  return (
    <div className="p-6 space-y-4" data-testid="ai-lead-manager-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Flame className="text-red-500" size={24} /> AI Lead Manager
          </h1>
          <div className="text-xs text-muted-foreground mt-1">
            AI-qualified sellers, prioritized. Data comes from Retell voice + AI SMS. Dialer and SMS Inbox stay where you work — this is where you decide who to work first.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
                data-testid="ai-lm-refresh-btn"
                className="rounded-full">
          <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Smart Lists — saved filters + inline rule builder. Applying
          a list constrains the queue below to only its lead IDs so reps
          can slice the AI queue by tag, motivation, city, etc. */}
      <div className="rounded-xl border-2 border-slate-100 bg-slate-50/40 p-3">
        <SmartListsBar
          onApply={({ id, name, count, leads }) => {
            const leadIds = (leads || []).map((l) => l.id || l.lead_id).filter(Boolean);
            setSmartFilter({ id, name, count, leadIds });
          }}
        />
        {smartFilter && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-fuchsia-100 border border-fuchsia-300 text-fuchsia-800 px-3 py-1 text-[11px] font-semibold"
               data-testid="ai-lm-smart-filter-chip">
            <Sparkles size={11} /> {smartFilter.name} · {smartFilter.count} matching
            <button onClick={() => setSmartFilter(null)}
                    className="ml-1 rounded-full hover:bg-black/10"
                    data-testid="ai-lm-clear-smart-filter"
                    aria-label="Clear smart list filter">
              <X size={11} />
            </button>
          </div>
        )}
      </div>

      {/* Bucket filter chips */}
      <div className="flex flex-wrap gap-2">
        {[{ key: "all", label: "All", tone: "" }, ...BUCKETS].map((b) => (
          <button
            key={b.key}
            onClick={() => setBkt(b.key)}
            data-testid={`ai-lm-bucket-${b.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 transition ${
              activeBucket === b.key
                ? "bg-slate-900 border-slate-900 text-white"
                : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
            }`}
          >
            {b.label} · {counts[b.key] ?? 0}
          </button>
        ))}
        <Input placeholder="Filter by name or address…"
               value={filter}
               onChange={(e) => setFilter(e.target.value)}
               className="ml-auto h-8 w-64 text-xs"
               data-testid="ai-lm-filter" />
      </div>

      {/* Queue table — capped to top 15 with scroll (rep asked for a
          predictable-height list so it doesn't push the page down). The
          full result set still lives in `filtered`; if a rep needs more
          they open the Smart Lists bar or the Boards page. */}
      <div className="rounded-2xl border-2 border-slate-200 overflow-hidden bg-white shadow-sm">
        <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-50 px-4 py-2.5 border-b">
          <div className="col-span-4">Lead / Property</div>
          <div className="col-span-2">Score</div>
          <div className="col-span-2">Latest Activity</div>
          <div className="col-span-3">AI Result</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        {filtered.length === 0 && !busy && (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">
            {rows.length === 0
              ? "No AI-qualified sellers yet. Once Retell or AI SMS handles a conversation, the highest-motivation sellers land here."
              : "No leads match your filter."}
          </div>
        )}
        <div className="max-h-[calc(15*72px)] overflow-y-auto" data-testid="ai-lm-scroll">
        {filtered.slice(0, 15).map((r) => {
          const bk = bucketOf(r.motivation_score || 0);
          const bucketMeta = BUCKETS.find((b) => b.key === bk);
          return (
            <div
              key={r.id}
              className={`grid grid-cols-12 items-center px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer transition ${bucketMeta.tone.replace("bg-", "hover:bg-")}`}
              onClick={() => openLead(r)}
              data-testid={`ai-lm-row-${r.id}`}
            >
              <div className="col-span-4">
                <div className="font-semibold text-sm truncate">{r.name || "Unknown Caller"}</div>
                <div className="text-[11px] text-muted-foreground truncate">{r.property_address || (r.from_number ? `from ${r.from_number}` : "—")}</div>
                {(r.score_reasons || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid={`ai-lm-reasons-${r.id}`}>
                    {(r.score_reasons || []).slice(0, 3).map((reason, idx) => {
                      const positive = reason.startsWith("+");
                      const negative = reason.startsWith("-");
                      const tone = positive
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : negative
                        ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-slate-100 text-slate-600 border-slate-200";
                      return (
                        <span key={idx}
                              className={`inline-flex items-center rounded-full border px-1.5 py-[1px] text-[9px] font-medium ${tone}`}
                              title={reason}>
                          {reason.length > 26 ? reason.slice(0, 25) + "…" : reason}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <span className={`inline-flex items-center gap-1 rounded-full border-2 px-2.5 py-0.5 text-[11px] font-bold ${bucketMeta.tone}`}>
                  {bk === "hot" && "🔥 "}{bk === "warm" && "🟠 "}{bk === "nurture" && "🟡 "}{bk === "low" && "⚪ "}
                  {r.motivation_score ?? 0} {bk.toUpperCase()}
                </span>
              </div>
              <div className="col-span-2 flex items-center gap-1.5 text-xs text-slate-700">
                {r.latest_kind === "sms" ? <MessageSquare size={12} className="text-emerald-600" /> : <Phone size={12} className="text-purple-600" />}
                <span className="capitalize truncate">{r.latest_kind || "—"}</span>
                <span className="text-muted-foreground">· {relTime(r.latest_at)}</span>
              </div>
              <div className="col-span-3 text-xs text-slate-700 truncate" title={r.ai_result || ""}>
                {r.ai_result || <span className="text-muted-foreground">Awaiting analysis…</span>}
              </div>
              <div className="col-span-1 flex items-center justify-end">
                <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-0.5">
                  {r.next_action || "Review"} <ChevronRight size={13} />
                </span>
              </div>
            </div>
          );
        })}
        </div>
      </div>
      <AILeadDetailDrawer
        leadId={openLeadId}
        unknownInboundId={openUnknownId}
        onClose={closeDrawer}
      />
    </div>
  );
}
